import { Lock, Mail } from "@mui/icons-material";
import SendIcon from "@mui/icons-material/Send";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import InputAdornment from "@mui/material/InputAdornment";
import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import React, { useContext, useState } from "react";
import { Helmet } from "react-helmet";
import logo from "../../assets/logo.png";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";

// Container principal com layout split
const LoginContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  position: "relative",
  overflow: "hidden",
  backgroundColor: theme.palette.mode === "dark" ? "#0F172A" : "#F8FAFC",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

// Lado esquerdo (split)
const LeftSection = styled(Box)(({ theme }) => ({
  flex: "0 0 60%",
  position: "relative",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(4),
  backgroundColor: "#1e293b",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    flex: "0 0 40%",
    minHeight: "40vh",
  },
}));

// Conteúdo do lado esquerdo
const LeftContent = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 2,
  textAlign: "center",
  color: "#F1F5F9",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(3),
}));

// Lado direito com formulário de login
const RightSection = styled(Box)(({ theme }) => ({
  flex: "0 0 40%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(6),
  backgroundColor:
    theme.palette.mode === "dark" ? "#0F172A" : "#F8FAFC",
  position: "relative",
  width: "100%",
  maxWidth: "600px",
  margin: "0 auto",
  [theme.breakpoints.down("md")]: {
    flex: "0 0 60%",
    minHeight: "60vh",
    maxWidth: "100%",
    padding: theme.spacing(4),
  },
}));

// TextField estilizado
const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "#F8FAFC",
    fontFamily: '"Inter", "Poppins", sans-serif',
    "& fieldset": {
      borderColor:
        theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.12)"
          : "#E2E8F0",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor:
        theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.2)"
          : "#CBD5E1",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#0B3C84",
      borderWidth: "1.5px",
    },
  },
  "& .MuiInputBase-input": {
    color: theme.palette.mode === "dark" ? "#F1F5F9" : "#1E293B",
    paddingLeft: "10px",
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: "14px",
  },
  "& .MuiInputLabel-root": {
    color:
      theme.palette.mode === "dark" ? "#94A3B8" : "#64748B",
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: "14px",
  },
  "& .MuiFormHelperText-root": {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: "12px",
  },
}));

// FormLabel estilizado
const StyledFormLabel = styled(FormLabel)(({ theme }) => ({
  color: theme.palette.mode === "dark" ? "#94A3B8" : "#64748B",
  marginBottom: "8px",
  fontFamily: '"Inter", "Poppins", sans-serif',
  fontSize: "14px",
  fontWeight: 500,
}));

// Link estilizado
const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.mode === "dark" ? "#94A3B8" : "#64748B",
  fontFamily: '"Inter", "Poppins", sans-serif',
  fontSize: "14px",
  textDecoration: "none",
  "&:hover": {
    color: "#0B3C84",
    textDecoration: "underline",
  },
}));

const Login = () => {
  const theme = useTheme();
  const [emailError, setEmailError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState("");
  const { colorMode } = useContext(ColorModeContext);
  const { appName } = colorMode;
  const [user, setUser] = useState({ email: "", password: "" });
  const { handleLogin } = useContext(AuthContext);

  const handleChangeInput = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handlSubmit = (e) => {
    e.preventDefault();
    handleLogin(user);
  };

  const validateInputs = () => {
    const email = document.getElementById("email");
    const password = document.getElementById("password");
    let isValid = true;

    if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
      setEmailError(true);
      setEmailErrorMessage("Por favor, insira um email válido.");
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage("");
    }

    if (!password.value || password.value.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage("A senha deve ter pelo menos 6 caracteres.");
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage("");
    }

    return isValid;
  };

  return (
    <>
      <Helmet>
        <title>{appName || "CRA - SC"}</title>
        <link rel="icon" href="/favicon.png" />
      </Helmet>
      <CssBaseline enableColorScheme />
      <LoginContainer>
        <LeftSection>
          <LeftContent>
            <Box>
              <img
                src={logo}
                alt="Logo"
                style={{
                  filter: "drop-shadow(0 4px 20px rgba(0, 0, 0, 0.35))",
                }}
              />
            </Box>
          </LeftContent>
        </LeftSection>

        {/* Lado direito com formulário de login */}
        <RightSection>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 4,
              gap: 1.5,
              width: "100%",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color:
                  theme.palette.mode === "dark" ? "#F1F5F9" : "#1E293B",
                fontWeight: 700,
                fontFamily: '"Inter", "Poppins", sans-serif',
                fontSize: { xs: "1.75rem", md: "2rem" },
              }}
            >
              Fazer Login
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color:
                  theme.palette.mode === "dark" ? "#94A3B8" : "#64748B",
                fontFamily: '"Inter", "Poppins", sans-serif',
                fontSize: "1rem",
              }}
            >
              Entre com suas credenciais
            </Typography>
          </Box>

          <Box
            component="form"
            onSubmit={handlSubmit}
            noValidate
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: 3.5,
            }}
          >
            <FormControl fullWidth>
              <StyledFormLabel htmlFor="email">E-mail</StyledFormLabel>
              <StyledTextField
                error={emailError}
                helperText={emailErrorMessage}
                id="email"
                type="email"
                name="email"
                placeholder="seu@email.com"
                autoComplete="email"
                autoFocus
                required
                fullWidth
                onChange={handleChangeInput}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Mail
                        sx={{
                          color:
                            theme.palette.mode === "dark"
                              ? "#94A3B8"
                              : "#64748B",
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>

            <FormControl fullWidth>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <StyledFormLabel htmlFor="password">Senha</StyledFormLabel>
                <StyledLink href="/recovery-password" variant="body2">
                  Esqueci minha senha
                </StyledLink>
              </Box>
              <StyledTextField
                error={passwordError}
                helperText={passwordErrorMessage}
                name="password"
                placeholder="••••••••"
                type="password"
                id="password"
                autoComplete="current-password"
                required
                fullWidth
                onChange={handleChangeInput}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock
                        sx={{
                          color:
                            theme.palette.mode === "dark"
                              ? "#94A3B8"
                              : "#64748B",
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              onClick={validateInputs}
              sx={{
                borderRadius: "12px",
                padding: "12px 24px",
                background: "#0B3C84",
                color: "#FFFFFF",
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
                fontFamily: '"Inter", "Poppins", sans-serif',
                boxShadow: "0 4px 12px rgba(11, 60, 132, 0.3)",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "#082D63",
                  boxShadow: "0 6px 16px rgba(11, 60, 132, 0.4)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0px)",
                },
              }}
              endIcon={<SendIcon />}
            >
              Entrar
            </Button>
          </Box>
        </RightSection>
      </LoginContainer>
    </>
  );
};

export default Login;
